import { useEffect, useRef } from 'react';

/**
 * A hook for having a callback invoked periodically, stopped after the component is unmounted.
 *
 * Based on https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 * License source https://github.com/gaearon/overreacted.io/blob/master/LICENSE-code-snippets
 * MIT License
 *
 * Copyright (c) 2020 Dan Abramov and the contributors.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
const useInterval = (callback: () => void, timeout: number) => {
  // We save the callback because the function that is registered with setInterval never changes. Once setInterval is
  // called with a particular function, that's the function that will be called until the interval is cleared. But we
  // want to be able to change the callback prop at any time while the interval stays alive.
  const savedCallback = useRef<() => void>();

  // Remember the new callback function every time it changes.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => savedCallback.current?.();
    const id = setInterval(tick, timeout);
    return () => clearInterval(id);
  }, [timeout]);
};

export default useInterval;
